import { useMemo, useState } from "react";
import {
  GraduationCap,
  Users,
  Calendar,
  Clock,
  Inbox,
  Pencil,
  UserX,
  UserCheck,
  Trash2,
  Mail,
  Phone,
} from "lucide-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import swal from "../../utils/swal";
import { UPLOADS_URL } from "../../config/constants";
import { useGetClassTimetables, useUpdateClassStatus } from "../../query/classQuery";
import { DetailCoverHero } from "../../components/DetailCoverHero";
import { classroomColorLabel, classroomColorTone } from "../../config/classroomColors";
import "../teachers/teacherDetails.css";
import "./classroom.css";

const WEEKDAYS = [
  { key: "MON", label: "Mon" },
  { key: "TUE", label: "Tue" },
  { key: "WED", label: "Wed" },
  { key: "THU", label: "Thu" },
  { key: "FRI", label: "Fri" },
];

const DAY_LABELS = {
  MON: "Monday",
  TUE: "Tuesday",
  WED: "Wednesday",
  THU: "Thursday",
  FRI: "Friday",
};

const LESSON_TONES = ["is-navy", "is-green", "is-yellow", "is-blue"];
const STUDENT_TONES = ["is-navy", "is-green", "is-yellow", "is-blue"];

function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase() || "C";
}

function teacherInitials(firstName = "", lastName = "") {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "T";
}

function todayDayKey() {
  const map = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const key = map[dayjs().day()];
  return WEEKDAYS.some((day) => day.key === key) ? key : "MON";
}

function formatSlotTime(start, end) {
  const parse = (value) => {
    if (!value) return "";
    const parsed = dayjs(`2000-01-01 ${value}`);
    return parsed.isValid() ? parsed.format("hh:mm A") : value;
  };
  const from = parse(start);
  const to = parse(end);
  if (from && to) return `${from} - ${to}`;
  return from || to || "Time not set";
}

export default function ClassProfile({ classroom, onDelete }) {
  const navigate = useNavigate();
  const [selectedDay, setSelectedDay] = useState(todayDayKey);
  const { data: timetable = {}, isLoading: isTimetableLoading } = useGetClassTimetables(classroom?._id);
  const { mutate: updateClassStatus, isPending: isUpdatingStatus } = useUpdateClassStatus();

  const students = classroom?.students || [];
  const teacher = classroom?.teacher;
  const teacherName = teacher
    ? `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim()
    : "";
  const isActive = classroom?.status === "ACTIVE";
  const statusLabel = classroom?.status === "ACTIVE" ? "Active" : "Inactive";
  const role = classroom?.classroomGrade
    ? `Grade ${classroom.classroomGrade} · Batch ${classroom.classroomBatch || "—"}`
    : "Classroom";

  const slots = useMemo(() => {
    const list = Array.isArray(timetable?.[selectedDay]) ? timetable[selectedDay] : [];
    return [...list].sort((a, b) => String(a.startTime || "").localeCompare(String(b.startTime || "")));
  }, [timetable, selectedDay]);

  const handleStatus = () => {
    const nextStatus = isActive ? "INACTIVE" : "ACTIVE";
    swal({
      title: "Are you sure?",
      text: `Do you want to ${nextStatus === "ACTIVE" ? "activate" : "deactivate"} this class?`,
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      updateClassStatus(
        { classId: classroom._id, status: nextStatus },
        {
          onSuccess: () => swal("Success", "Status updated successfully", "success"),
          onError: (error) => swal("Error", error?.message || "Failed to update status", "error"),
        }
      );
    });
  };

  return (
    <div className="td-page">
      <div className="td-main">
        <article className="td-card">
          <DetailCoverHero
            coverKey={classroom?._id}
            backLabel="Back to classes"
            onBack={() => navigate("/classes")}
          />

          <div className="td-body">
            <div className="td-identity">
              <span className={`td-avatar ${classroomColorTone(classroom?.color)}`}>
                {initials(classroom?.classroomName)}
              </span>
              <div className="td-identity-row">
                <h2>{classroom?.classroomName || "Class"}</h2>
                <span className={`td-status is-${(classroom?.status || "inactive").toLowerCase()}`}>
                  {statusLabel}
                </span>
              </div>
              <p>{classroom?.classroomId ? `${role} · ${classroom.classroomId}` : role}</p>
            </div>

            <div className="td-chips">
              <span>
                <i className={classroomColorTone(classroom?.color)}>
                  <GraduationCap size={14} />
                </i>
                {classroomColorLabel(classroom?.color)}
              </span>
              <span>
                <i className="is-orange">
                  <Users size={14} />
                </i>
                {teacherName || "No teacher assigned"}
              </span>
              <span>
                <i className="is-orange">
                  <Users size={14} />
                </i>
                {students.length} {students.length === 1 ? "student" : "students"}
              </span>
              <span>
                <i className="is-orange">
                  <Calendar size={14} />
                </i>
                Batch {classroom?.classroomBatch || "—"}
              </span>
            </div>

            <section className="td-section">
              <h3>About:</h3>
              {classroom?.description ? (
                <p>{classroom.description}</p>
              ) : (
                <div className="td-empty">
                  <Inbox size={22} />
                  <span>No description yet</span>
                </div>
              )}
            </section>

            <section className="td-section">
              <h3>Homeroom teacher:</h3>
              {teacher ? (
                <ul>
                  <li>
                    <strong>{teacherName}</strong>
                    <em>{teacher.teacherId ? `#${teacher.teacherId}` : "Teacher"}</em>
                  </li>
                  {teacher.email ? (
                    <li>
                      <strong>
                        <Mail size={13} style={{ display: "inline", marginRight: 6 }} />
                        {teacher.email}
                      </strong>
                    </li>
                  ) : null}
                  {teacher.phone ? (
                    <li>
                      <strong>
                        <Phone size={13} style={{ display: "inline", marginRight: 6 }} />
                        {teacher.phone}
                      </strong>
                    </li>
                  ) : null}
                </ul>
              ) : (
                <div className="td-empty">
                  <Inbox size={22} />
                  <span>No homeroom teacher assigned</span>
                </div>
              )}
            </section>

            <section className="td-section">
              <h3>Students:</h3>
              {students.length ? (
                <ul>
                  {students.map((student) => (
                    <li key={student._id}>
                      <strong>
                        {`${student.firstName || ""} ${student.lastName || ""}`.trim() || "Student"}
                      </strong>
                      <em>{student.rollNumber || "No roll no."}</em>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="td-empty">
                  <Inbox size={22} />
                  <span>No students assigned yet</span>
                </div>
              )}
            </section>
          </div>
        </article>

        <div className="td-actions">
          <button
            type="button"
            className="td-action is-primary"
            onClick={() => navigate(`/classes/edit/${classroom._id}`)}
          >
            <Pencil size={15} />
            Edit details
          </button>
          <button
            type="button"
            className="td-action"
            disabled={isUpdatingStatus}
            onClick={handleStatus}
          >
            {isActive ? <UserX size={15} /> : <UserCheck size={15} />}
            {isActive ? "Deactivate" : "Activate"}
          </button>
          <button type="button" className="td-action is-danger" onClick={onDelete}>
            <Trash2 size={15} />
            Delete
          </button>
        </div>
      </div>

      <aside className="td-schedule">
        <div className="td-schedule-head">
          <h3>Timetable</h3>
          <p>
            {classroom?.classroomName || "Class"}
            {" · "}
            {DAY_LABELS[selectedDay]}
          </p>
        </div>

        <div className="td-days">
          {WEEKDAYS.map((day) => (
            <button
              key={day.key}
              type="button"
              className={selectedDay === day.key ? "is-active" : ""}
              onClick={() => setSelectedDay(day.key)}
            >
              {day.label}
            </button>
          ))}
        </div>

        {isTimetableLoading ? (
          <div className="td-empty">
            <span>Loading timetable…</span>
          </div>
        ) : slots.length ? (
          <div className="td-schedule-list">
            {slots.map((item, index) => (
              <article
                key={item._id || `${item.subject}-${index}`}
                className={`td-lesson ${LESSON_TONES[index % LESSON_TONES.length]}`}
              >
                <div className="td-lesson-copy">
                  <h4>{item.subject || "Period"}</h4>
                  <p>{item.description || classroom?.classroomName || "Classroom"}</p>
                  <div className="td-lesson-meta">
                    <span>
                      <i>
                        <Calendar size={13} />
                      </i>
                      {DAY_LABELS[item.day] || DAY_LABELS[selectedDay]}
                    </span>
                    <span>
                      <i>
                        <Clock size={13} />
                      </i>
                      {formatSlotTime(item.startTime, item.endTime)}
                    </span>
                  </div>
                </div>
                {teacher?.image ? (
                  <img className="td-lesson-avatar" src={`${UPLOADS_URL}/${teacher.image}`} alt="" />
                ) : (
                  <span className="td-lesson-avatar">
                    {teacherInitials(teacher?.firstName, teacher?.lastName)}
                  </span>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="td-empty">
            <Inbox size={22} />
            <span>No periods on {DAY_LABELS[selectedDay]}</span>
          </div>
        )}

        {students.length ? (
          <>
            <div className="td-schedule-head" style={{ marginTop: 24 }}>
              <h3>Students</h3>
              <p>{students.length} enrolled</p>
            </div>
            <div className="td-schedule-list">
              {students.map((student, index) => {
                const name = `${student.firstName || ""} ${student.lastName || ""}`.trim() || "Student";
                return (
                  <article
                    key={student._id}
                    className={`td-lesson ${STUDENT_TONES[index % STUDENT_TONES.length]}`}
                    onClick={() => navigate(`/children/${student._id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="td-lesson-copy">
                      <h4>{name}</h4>
                      <p>{student.rollNumber || "No roll no."}</p>
                    </div>
                    {student?.image ? (
                      <img className="td-lesson-avatar" src={`${UPLOADS_URL}/${student.image}`} alt="" />
                    ) : (
                      <span className="td-avatar td-lesson-avatar">{initials(name)}</span>
                    )}
                  </article>
                );
              })}
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
}
